import React, { useCallback } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import useMapView from "@app/component/view/utils/useMapView";
import ProcessingModal from "@app/scene/map/components/LeadFinderPopover/components/ProcessingModal";
import SearchAreaBar from "@app/scene/map/components/LeadFinderPopover/components/SearchAreaBar";
import { exitLeadFinderMode, saveSearch } from "@app/scene/map/store/actions";
import {
  getLeadFinderSavedSearches,
  getLeadFinderSearch,
  getMapLassoPathsCount,
} from "@app/scene/map/store/selectors";
import { getLeadGenPlan, isCurrentUserOwner, isLeadFinderSaveEnabled } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import SavedSearchesModal from "./SavedSearchesModal";
import styles from "./TopPanel.module.scss";

const messages = defineMessages({
  editSearchTerm: {
    id: "map.tool.leadFinder.toolbar.editSearchTerm",
    defaultMessage: "Edit search term",
    description: "Edit search term button title",
  },
  exit: {
    id: "map.tool.leadFinder.toolbar.exitButton",
    defaultMessage: "Exit Lead Finder",
    description: "Exit Lead Finder button title",
  },
  save: {
    id: "map.tool.leadFinder.toolbar.saveButton",
    defaultMessage: "Save this search",
    description: "Save this search button title",
  },
  searchText: {
    id: "map.tool.leadFinder.toolbar.searchText",
    defaultMessage: "{query} around ",
    description: "Lead Finder search around title",
  },
});

interface Props {
  canSave: boolean;
  isOwner: boolean;
  leadGenPlan?: LeadFinderSubscriptionPlan;
  onExitLeadFinderMode: () => void;
  pathsCount: number;
  query?: string;
  savedSearches: Array<string>;
  saveSearch: typeof saveSearch;
}

const TopPanel: React.FC<Props> = ({
  canSave,
  isOwner,
  leadGenPlan,
  onExitLeadFinderMode,
  pathsCount,
  query,
  savedSearches,
  saveSearch,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const { viewState } = useMapView();

  const isVisible = !(viewState.tool === MapTool.LASSO && !pathsCount);

  const handleSaveSearch = useCallback(() => {
    if (canSave && query) {
      saveSearch({ search: query });
    }
    analytics.clicked(["Save this search"]);
    if (!canSave) {
      showLeadFinderUpsell(leadGenPlan, intl, isOwner);
    }
  }, [canSave, query, analytics, leadGenPlan, intl, isOwner, saveSearch]);

  const isSaveVisible = !!query && !savedSearches.includes(query);

  const handleExitLeadFinderMode = useCallback(() => {
    onExitLeadFinderMode();
    analytics.cancelled("");
  }, [analytics, onExitLeadFinderMode]);

  return isVisible ? (
    <div className={styles.container}>
      <div className={styles.content}>
        {query && (
          <Interactive>
            <SearchAreaBar analyticsIssuer={analytics} />
            <ProcessingModal />
          </Interactive>
        )}
      </div>

      <div className={styles.action}>
        {isSaveVisible && (
          <Interactive>
            <Button onClick={handleSaveSearch} type="filled">
              {intl.formatMessage(messages.save)}
            </Button>
          </Interactive>
        )}

        <Interactive>
          <Button onClick={handleExitLeadFinderMode}>{intl.formatMessage(messages.exit)}</Button>
        </Interactive>

        <SavedSearchesModal />
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  canSave: isLeadFinderSaveEnabled(state),
  isOwner: isCurrentUserOwner(state),
  leadGenPlan: getLeadGenPlan(state),
  pathsCount: getMapLassoPathsCount(state),
  query: getLeadFinderSearch(state),
  savedSearches: getLeadFinderSavedSearches(state),
});

const mapDispatchToProps = {
  onExitLeadFinderMode: exitLeadFinderMode,
  saveSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(TopPanel);
